<!--
  NOTE @chris
  This component is intentionally not scoped to be able to overwrite some vuetify defaults conflicting quill editor. Component will account for the difference between quill editor and markup saved.

  A different approach would be to use blots, but this brakes on conditional renders inside vue:
  https://stackoverflow.com/questions/63489524/extending-quill-to-support-soft-line-breaks
-->
<template>
  <div class="sx-html" v-html="parsedText" />
</template>

<script>
export default {
  name: 'SxHtml',
  computed: {
    parsedText() {
      if (!this.text) return '';

      // NOTE @chris Quill doesn't support iframes but we need to let people add videos to their engagement letters and custom sections. The below is fine until we're treating firms as trusted entities but we need to replace this with a safer wysiwyg editor at some point.
      return this.text.replaceAll('&lt;', '<').replaceAll('&gt;', '>');
    },
  },
  props: {
    text: {
      type: String,
      default: '',
    },
  },
};
</script>

<style>
/* NOTE @chris This is only necessary because we want this component to cascade, which means it can be cascaded into as well. */
.sx-html {
  white-space: normal !important;
}

/* NOTE @chris Better than br:only-child because it supports simple line brakes. We need important to overwrite vuetify, then our own on top of that for variants. */
.sx-html p,
.sx-html ol,
.sx-html ul {
  display: block;
  margin-bottom: 0 !important;

  text-align: left !important;
}

.sx-html .center {
  display: block;

  text-align: center !important;
}

.sx-html iframe {
  display: flex;
  margin: 0 auto;
  max-width: 100%;

  border-radius: 4px;
}

.sx-html p:last-child br:only-child {
  display: none;
}

.sx-html ol,
.sx-html ul {
  padding-left: 1.2em;

  list-style-position: outside;
}

.sx-html ol {
  counter-reset: list-0;
  list-style: none;
}

.sx-html ul {
  list-style: none;
}

.sx-html ul li:before {
  position: absolute;

  margin-left: -1em;

  content: '•';
}

.sx-html ol li {
  counter-increment: list-0;
}

.sx-html ol li:before {
  content: counter(list-0, decimal) '. ';
}

.sx-html ol li.ql-indent-1 {
  counter-increment: list-1;
  counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
}

.sx-html ol li.ql-indent-1:before {
  content: counter(list-1, lower-alpha) '. ';
}

.sx-html ol li.ql-indent-2 {
  counter-increment: list-2;
  counter-reset: list-3 list-4 list-5 list-6 list-7 list-8 list-9;
}

.sx-html ol li.ql-indent-2:before {
  content: counter(list-2, lower-roman) '. ';
}

.sx-html ol li.ql-indent-3 {
  counter-increment: list-3;
  counter-reset: list-4 list-5 list-6 list-7 list-8 list-9;
}

.sx-html ol li.ql-indent-3:before {
  content: counter(list-3, decimal) '. ';
}

.sx-html ol li.ql-indent-4 {
  counter-increment: list-4;
  counter-reset: list-5 list-6 list-7 list-8 list-9;
}

.sx-html ol li.ql-indent-4:before {
  content: counter(list-4, lower-alpha) '. ';
}

.sx-html ol li.ql-indent-5 {
  counter-increment: list-5;
  counter-reset: list-6 list-7 list-8 list-9;
}

.sx-html ol li.ql-indent-5:before {
  content: counter(list-5, lower-roman) '. ';
}

.sx-html ol li.ql-indent-6 {
  counter-increment: list-6;
  counter-reset: list-7 list-8 list-9;
}

.sx-html ol li.ql-indent-6:before {
  content: counter(list-6, decimal) '. ';
}

.sx-html ol li.ql-indent-7 {
  counter-increment: list-7;
  counter-reset: list-8 list-9;
}

.sx-html ol li.ql-indent-7:before {
  content: counter(list-7, lower-alpha) '. ';
}

.sx-html ol li.ql-indent-8 {
  counter-increment: list-8;
  counter-reset: list-9;
}

.sx-html ol li.ql-indent-8:before {
  content: counter(list-8, lower-roman) '. ';
}

.sx-html ol li.ql-indent-9 {
  counter-increment: list-9;
}

.sx-html ol li.ql-indent-9:before {
  content: counter(list-9, decimal) '. ';
}

.sx-html .ql-indent-1:not(.ql-direction-rtl) {
  padding-left: 3em;
}

.sx-html li.ql-indent-1:not(.ql-direction-rtl) {
  padding-left: 4.5em;
}

.sx-html .ql-indent-1.ql-direction-rtl.ql-align-right {
  padding-right: 3em;
}

.sx-html li.ql-indent-1.ql-direction-rtl.ql-align-right {
  padding-right: 4.5em;
}

.sx-html .ql-indent-2:not(.ql-direction-rtl) {
  padding-left: 6em;
}

.sx-html li.ql-indent-2:not(.ql-direction-rtl) {
  padding-left: 7.5em;
}

.sx-html .ql-indent-2.ql-direction-rtl.ql-align-right {
  padding-right: 6em;
}

.sx-html li.ql-indent-2.ql-direction-rtl.ql-align-right {
  padding-right: 7.5em;
}

.sx-html .ql-indent-3:not(.ql-direction-rtl) {
  padding-left: 9em;
}

.sx-html li.ql-indent-3:not(.ql-direction-rtl) {
  padding-left: 10.5em;
}

.sx-html .ql-indent-3.ql-direction-rtl.ql-align-right {
  padding-right: 9em;
}

.sx-html li.ql-indent-3.ql-direction-rtl.ql-align-right {
  padding-right: 10.5em;
}

.sx-html .ql-indent-4:not(.ql-direction-rtl) {
  padding-left: 12em;
}

.sx-html li.ql-indent-4:not(.ql-direction-rtl) {
  padding-left: 13.5em;
}

.sx-html .ql-indent-4.ql-direction-rtl.ql-align-right {
  padding-right: 12em;
}

.sx-html li.ql-indent-4.ql-direction-rtl.ql-align-right {
  padding-right: 13.5em;
}

.sx-html .ql-indent-5:not(.ql-direction-rtl) {
  padding-left: 15em;
}

.sx-html li.ql-indent-5:not(.ql-direction-rtl) {
  padding-left: 16.5em;
}

.sx-html .ql-indent-5.ql-direction-rtl.ql-align-right {
  padding-right: 15em;
}
.sx-html li.ql-indent-5.ql-direction-rtl.ql-align-right {
  padding-right: 16.5em;
}

.sx-html .ql-indent-6:not(.ql-direction-rtl) {
  padding-left: 18em;
}

.sx-html li.ql-indent-6:not(.ql-direction-rtl) {
  padding-left: 19.5em;
}

.sx-html .ql-indent-6.ql-direction-rtl.ql-align-right {
  padding-right: 18em;
}

.sx-html li.ql-indent-6.ql-direction-rtl.ql-align-right {
  padding-right: 19.5em;
}

.sx-html .ql-indent-7:not(.ql-direction-rtl) {
  padding-left: 21em;
}

.sx-html li.ql-indent-7:not(.ql-direction-rtl) {
  padding-left: 22.5em;
}

.sx-html .ql-indent-7.ql-direction-rtl.ql-align-right {
  padding-right: 21em;
}

.sx-html li.ql-indent-7.ql-direction-rtl.ql-align-right {
  padding-right: 22.5em;
}

.sx-html .ql-indent-8:not(.ql-direction-rtl) {
  padding-left: 24em;
}

.sx-html li.ql-indent-8:not(.ql-direction-rtl) {
  padding-left: 25.5em;
}

.sx-html .ql-indent-8.ql-direction-rtl.ql-align-right {
  padding-right: 24em;
}

.sx-html li.ql-indent-8.ql-direction-rtl.ql-align-right {
  padding-right: 25.5em;
}

.sx-html .ql-indent-9:not(.ql-direction-rtl) {
  padding-left: 27em;
}

.sx-html li.ql-indent-9:not(.ql-direction-rtl) {
  padding-left: 28.5em;
}

.sx-html .ql-indent-9.ql-direction-rtl.ql-align-right {
  padding-right: 27em;
}

.sx-html li.ql-indent-9.ql-direction-rtl.ql-align-right {
  padding-right: 28.5em;
}
</style>
